import { useCallback, useEffect, useState } from 'react'
import { Edit3, Images, Info, Plus, Trash2 } from 'lucide-react'
import type { VisualAsset, VisualGroup } from '../../api/visuals'
import { Button, DataTableElement, DataTableSurface, EmptyState, IconButton, Pill, RowActions, SearchField } from '../../components/ui'

type VisualGroupsProps = {
  assets: VisualAsset[]
  groups: VisualGroup[]
  onCreate: () => void
  onDelete: (group: VisualGroup) => Promise<void>
  onEdit: (group: VisualGroup) => void
  onManageAssets: (group: VisualGroup) => void
  onRegisterMobileCreateAction?: (action: (() => void) | null) => void
}

export const VisualGroups = (props: VisualGroupsProps) => {
  const [search, setSearch] = useState('')
  const [openInfoGroupId, setOpenInfoGroupId] = useState<string | null>(null)
  const registerMobileCreateAction = props.onRegisterMobileCreateAction
  const onCreate = props.onCreate
  const visibleGroups = props.groups.filter((group) => {
    const query = search.trim().toLowerCase()

    return !query
      || group.name.toLowerCase().includes(query)
      || (group.description ?? '').toLowerCase().includes(query)
  })
  const openCreateDialog = useCallback((): void => {
    onCreate()
  }, [onCreate])

  useEffect(() => {
    registerMobileCreateAction?.(openCreateDialog)

    return () => {
      registerMobileCreateAction?.(null)
    }
  }, [openCreateDialog, registerMobileCreateAction])

  useEffect(() => {
    if (!openInfoGroupId) {
      return undefined
    }

    const closeInfoPopover = (event: PointerEvent): void => {
      const target = event.target

      if (target instanceof Element && target.closest('.group-actions-cell')) {
        return
      }

      setOpenInfoGroupId(null)
    }

    document.addEventListener('pointerdown', closeInfoPopover)

    return () => {
      document.removeEventListener('pointerdown', closeInfoPopover)
    }
  }, [openInfoGroupId])

  return (
    <>
      <div className="toolbar visual-toolbar">
        <Button className="toolbar-upload group-create-button" mobileFullWidth onClick={props.onCreate} variant="primary">
          <Plus size={16} /> Create visual group
        </Button>
        <SearchField
          className="toolbar-search-field"
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search visual groups..."
          value={search}
        />
      </div>

      <DataTableSurface className="visual-groups-table-card" fullWidth mobileStack mobileVariant="entityList">
        <DataTableElement>
          <thead>
            <tr>
              <th>Name</th>
              <th>Assets</th>
              <th>Channels</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleGroups.map((group) => {
              const infoOpen = openInfoGroupId === group.id

              return (
              <tr key={group.id}>
                <td className="group-title-cell" data-label="">
                  <span className="group-title-line">
                    <strong>{group.name}</strong>
                    <Pill strong>{group.asset_count}</Pill>
                  </span>
                  {group.description && <span>{group.description}</span>}
                </td>
                <td className="group-secondary-cell" data-label="Assets"><Pill strong>{group.asset_count}</Pill></td>
                <td className="group-secondary-cell" data-label="Channels"><Pill strong>{group.channel_count}</Pill></td>
                <td className="group-secondary-cell" data-label="Updated">{new Date(group.updated_at).toLocaleString()}</td>
                <td className="group-actions-cell" data-label="Actions">
                  <RowActions className="group-mobile-actions" mobileStack>
                    <IconButton
                      aria-expanded={infoOpen}
                      icon={<Info size={15} />}
                      label={`Group info for ${group.name}`}
                      onClick={() => setOpenInfoGroupId((current) => current === group.id ? null : group.id)}
                      title="Group info"
                    />
                  </RowActions>
                  <RowActions className="group-inline-actions" mobileStack>
                    <IconButton
                      icon={<Edit3 size={15} />}
                      label={`Edit ${group.name}`}
                      onClick={() => props.onEdit(group)}
                      title="Edit"
                    />
                    <IconButton
                      disabled={props.assets.length === 0}
                      icon={<Images size={15} />}
                      label={`Manage assets in ${group.name}`}
                      onClick={() => props.onManageAssets(group)}
                      title="Manage assets"
                    />
                    <IconButton
                      icon={<Trash2 size={15} />}
                      label={`Delete ${group.name}`}
                      onClick={() => void props.onDelete(group)}
                      title="Delete"
                      variant="danger"
                    />
                  </RowActions>
                  {infoOpen && (
                    <div className="track-info-popover group-info-popover" role="dialog" aria-label={`Details for ${group.name}`}>
                      <div>
                        <span>Channels</span>
                        <strong>{group.channel_count}</strong>
                      </div>
                      <div className="track-info-actions">
                        <IconButton
                          icon={<Edit3 size={15} />}
                          label={`Edit ${group.name}`}
                          onClick={() => {
                            setOpenInfoGroupId(null)
                            props.onEdit(group)
                          }}
                          title="Edit"
                        />
                        <IconButton
                          disabled={props.assets.length === 0}
                          icon={<Images size={15} />}
                          label={`Manage assets in ${group.name}`}
                          onClick={() => {
                            setOpenInfoGroupId(null)
                            props.onManageAssets(group)
                          }}
                          title="Manage assets"
                        />
                        <IconButton
                          icon={<Trash2 size={15} />}
                          label={`Delete ${group.name}`}
                          onClick={() => {
                            setOpenInfoGroupId(null)
                            void props.onDelete(group)
                          }}
                          title="Delete"
                          variant="danger"
                        />
                      </div>
                    </div>
                  )}
                </td>
              </tr>
              )
            })}
          </tbody>
        </DataTableElement>
        {visibleGroups.length === 0 && <EmptyState message="No visual groups match this search." />}
      </DataTableSurface>
    </>
  )
}
